import { transformDocumentNumber } from '@/components/custom/erp';
import { isValidUUID } from '@/lib/uuid-validator';
import { toFiniteNumber } from '@/lib/formatUtils';
import { FormFieldState } from '../hooks';

/**
 * Transform detail rows for API submission
 * @param detailRows - Array of detail row data from the form
 * @returns Transformed detail rows ready for API submission
 */
export const transformDetailRows = (
  detailRows: BanHangRecord[],
  formMode: string,
  state: FormFieldState,
  initialDetailRows?: BanHangRecord[]
) => {
  const details = detailRows.map((row: BanHangRecord, index: number) => {
    const initialRow = initialDetailRows?.find((ct: BanHangRecord) => ct.uuid === row.uuid);
    return {
      line: index + 1,
      ...(formMode !== 'add' && isValidUUID(row.uuid) ? { uuid: row.uuid } : {}),
      sl_cl: toFiniteNumber(row.so_luong) - toFiniteNumber(initialRow?.so_luong) + toFiniteNumber(initialRow?.sl_cl),
      ma_vt: row.ma_vt_data?.uuid || '',
      dvt: row.dvt || row.ma_vt_data?.dvt || '',
      ma_kho: row.ma_kho_data?.uuid || null,
      ct_km: row.ct_km || '0',
      so_luong: Number(row.so_luong),
      gia_nt1: Number(row.gia_nt1) || 0,
      gia_nt2: Number(row.gia_nt2) || 0,
      tien_nt2: Number(row.tien_nt2) || 0,
      px_dd: row.px_dd,
      gia_nt: Number(row.gia_nt) || 0,
      tien_nt: Number(row.tien_nt) || 0,
      gia: Number(row.gia_nt) || 0,
      tien: Number(row.tien_nt) || 0,
      gia1: Number(row.gia_nt1) || 0,
      gia2: Number(row.gia_nt2) || 0,
      tien2: Number(row.tien_nt2) || 0,
      ma_thue: row.ma_thue_data?.uuid || '',
      thue_nt: Number(row.thue_nt) || 0,
      thue_suat: row.ma_thue_data?.thue_suat || 0,
      tk_thue_co: row.tk_thue_co_data?.uuid || '',
      tk_dt: row.tk_dt_data?.uuid || row.ma_vt_data?.tk_dt_data?.uuid || '',
      tk_gv: row.tk_gv_data?.uuid || row.ma_vt_data?.tk_gv_data?.uuid || '',
      tk_vt: row.tk_vt_data?.uuid || row.ma_vt_data?.tk_vt_data?.uuid || '',
      tk_ck: row.tk_ck_data?.uuid || row.ma_vt_data?.tk_ck_data?.uuid || '',
      tk_km: row.tk_km_data?.uuid || row.ma_vt_data?.tk_km_data?.uuid || '',
      ghi_chu: row.ghi_chu || '',
      ma_bp: row.ma_bp_data?.uuid || null,
      ma_vv: row.ma_vv_data?.uuid || null,
      ma_hd: row.ma_hd_data?.uuid || null,
      ma_dtt: row.ma_dtt_data?.uuid || null,
      ma_ku: row.ma_ku_data?.uuid || null,
      ma_phi: row.ma_phi_data?.uuid || null,
      ma_sp: row.ma_sp_data?.uuid || null,
      ma_lsx: row.ma_lsx_data?.uuid || null,
      ma_cp0: row.ma_cp0_data?.uuid || null,
      id_dh: row.id_dh?.uuid || row.id_hd || null,
      sl_px: Number(row.sl_px) || null,
      line_dh: row.line_dh?.uuid || null,
      line_hd: row.line_hd || null,
      tl_ck: state.ck_yn ? row.tl_ck : 0,
      ck_nt: state.ck_yn ? row.ck_nt : 0
    };
  });

  return details;
};

/**
 * Transform account rows for API submission
 * @param accountRows - Array of account row data from the form
 * @returns Transformed account rows ready for API submission
 */
export const transformAccountRows = (accountRows: BanHangRecord[], formMode: string) => {
  return accountRows.map((row: BanHangRecord, index: number) => ({
    line: index + 1,
    ...(isValidUUID(row.uuid) && formMode !== 'add' ? { uuid: row.uuid } : {}),
    ma_httt: row.ma_httt_data?.uuid || '',
    ten_httt: row.ma_httt_data?.ten_httt || '',
    tknh: row.tknh || '',
    tk: row.tk_data?.uuid || '',
    ma_ct: row.ma_ct_data?.uuid || '',
    ngay_ct: row.ngay_ct || '',
    ma_nk: row.ma_nk_data?.uuid || '',
    t_tt_nt: row.t_tt_nt || 0,
    id_ct_tt: row.ma_ct_data?.uuid || ''
  }));
};

/**
 * Transform all form data for submission
 * @param data - Form data from the form submission
 * @param state - Form field state containing references to selected entities
 * @param detailRows - Array of detail row data
 * @param accountRows - Array of account row data
 * @param totals - Calculated totals from calculateTotals function
 * @returns Transformed data ready for API submission
 */
export const transformFormData = (
  data: BanHangPayload,
  state: FormFieldState,
  detailRows: BanHangRecord[] = [],
  accountRows: BanHangRecord[] = [],
  formMode: string,
  totals: BanHangRecord,
  entityUnit?: BanHangRecord,
  initialDetailRows?: BanHangRecord[]
) => {
  const detail = transformDetailRows(detailRows, formMode, state, initialDetailRows);
  const account = transformAccountRows(accountRows, formMode);

  const hasDetailRows = detailRows && detailRows.length > 0;

  const {
    tong_sl = 0,
    tong_tien = 0,
    tong_thue = 0,
    tong_tien_von = 0,
    tong_thanh_toan = 0,
    tong_ck = 0
  } = totals || {};

  const formData: BanHangPayload = {
    ...data,
    ngay_bk: data.ngay_bk || null,

    hdbh_yn: state.hdbh_yn,
    px_yn: state.px_yn,
    pt_tao_yn: state.pt_tao_yn,
    ck_yn: state.ck_yn,
    loai_ck: state.ck_yn ? state.loai_ck : 4,
    ck_tl_nt: state.ck_tl_nt || 0,

    ma_httt: state.ma_httt || 'TMB',

    ma_kh: state.khachHang?.uuid || '',
    ma_so_thue: data.ma_so_thue || state.khachHang?.tax_code || '',
    ten_kh_thue: data.ten_kh_thue || state.khachHang?.customer_name || '',
    dia_chi: data.dia_chi || state.khachHang?.address || state.khachHang?.dia_chi || '',
    ong_ba: data.ong_ba || state.khachHang?.contact_person || '',
    e_mail: data.e_mail || state.khachHang?.email || '',
    dien_giai: data.dien_giai || state.khachHang?.description || '',

    ma_nvbh: state.nhanVien?.uuid || state.khachHang?.sales_rep_data?.uuid || '',
    tk: state.taiKhoan?.uuid || state.khachHang?.account_data?.uuid || '',
    ma_tt: state.hanThanhToan?.uuid || state.khachHang?.payment_term_data?.uuid || '',
    ma_dc: state.diaChi?.uuid || '',
    ma_ptvc: state.phuongTienVanChuyen?.uuid || '',
    ma_ptgh: state.phuongTienGiaoHang?.uuid || '',
    ma_kh9: state.cucThue?.uuid || '',

    ma_ngv: '1',
    ...transformDocumentNumber(state.quyenChungTu, state.soChungTu),
    i_so_px: transformDocumentNumber(state.quyenPhieuXuat, state.soPhieuXuat || '').i_so_ct,
    ma_nk_px: transformDocumentNumber(state.quyenPhieuXuat, state.soPhieuXuat || '').ma_nk,
    so_px: transformDocumentNumber(state.quyenPhieuXuat, state.soPhieuXuat || '').so_ct,
    ngay_px: data.ngay_px || '',
    so_ct2: data.so_ct2,
    so_ct0: data.so_ct0,
    ngay_ct: data.ngay_ct || '',
    ngay_lct: data.ngay_ct || '',

    so_ct_hddt0: data.so_ct_hddt0 || '',
    ngay_ct_hddt0: data.ngay_ct_hddt0 || '',
    so_ct2_hddt: data.so_ct2_hddt || '',

    t_so_luong: tong_sl,
    t_tien_nt2: tong_tien,
    t_tien2: tong_tien,
    t_thue_nt: tong_thue,
    t_thue: tong_thue,
    t_ck_nt: tong_ck,
    t_ck: tong_ck,
    t_tt_nt: tong_thanh_toan,
    t_tt: tong_thanh_toan,
    t_tien_nt: tong_tien_von,
    t_tien: tong_tien_von,

    unit_id: entityUnit?.uuid,

    thong_tin_thanh_toan: account
  };

  if (hasDetailRows) {
    formData.chi_tiet = detail;
  }

  return formData;
};
